import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuditApiService } from '../../../camera-audit-live/core/audit-api.service';
import {
	formatBags,
	formatPct,
	MachineLevelHistory,
	RESTOCK_RANGES,
	RestockComparison,
	RestockComparisonResponse,
	RestockRange,
	restockTime
} from '../../../../../core/machine-camera/_services/machine-camera.util';

export interface CameraHistoryDialogData {
	machineId: string;
	slotNo: number;
	name: string;
	// BrewVision readings are offered only where the machine has BrewVision and they are shown.
	showBrewVision: boolean;
}

export type HistoryView = 'both' | 'brewvision' | 'machine';

const BREWVISION_COLOR = '#6366f1'; // indigo-500
const MACHINE_COLOR = '#475569'; // slate-600
const RESTOCK_COLOR = '#94a3b8'; // slate-400

// One canister's level over time for super admins on the Inventory page: the machine's own level,
// and BrewVision's readings where there are any, with restocks marked. The camera audit pages have
// BrewVision's full detail.
@Component({
	selector: 'kt-camera-history-dialog',
	templateUrl: './camera-history-dialog.component.html',
	styleUrls: ['./camera-history-dialog.component.scss']
})
export class CameraHistoryDialogComponent implements OnInit, OnDestroy {
	readonly ranges = RESTOCK_RANGES;
	readonly views: { value: HistoryView; label: string }[] = [
		{ value: 'both', label: 'Both' },
		{ value: 'brewvision', label: 'BrewVision' },
		{ value: 'machine', label: 'Machine' }
	];

	range: RestockRange = '14d';
	view: HistoryView;
	isLoading = false;
	loadError = '';
	chart: Record<string, any> = null;
	hasData = false;

	private readings: { x: number; y: number }[] = [];
	private machineLevels: { x: number; y: number | null }[] = [];
	private restocks: RestockComparison[] = [];
	private loadSubscription: Subscription = null;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: CameraHistoryDialogData,
		private cameraApi: AuditApiService,
		private cdr: ChangeDetectorRef
	) {
		this.view = data.showBrewVision ? 'both' : 'machine';
	}

	ngOnInit(): void {
		this.load();
	}

	ngOnDestroy(): void {
		if (this.loadSubscription) {
			this.loadSubscription.unsubscribe();
		}
	}

	get rangeDays(): string {
		return this.range.replace('d', '');
	}

	onRangeChange(range: RestockRange): void {
		if (range === this.range) {
			return;
		}
		this.range = range;
		this.load();
	}

	onViewChange(view: HistoryView): void {
		this.view = view;
		this.render();
	}

	private load(): void {
		if (this.loadSubscription) {
			this.loadSubscription.unsubscribe();
		}
		this.isLoading = true;
		this.loadError = '';
		const machineId = this.data.machineId;
		// Each part draws on its own: one that fails is left out, and only nothing at all is an error.
		const orNull = <T>(source: Observable<T>): Observable<T | null> => source.pipe(catchError(() => of(null)));
		const readings$: Observable<any> = this.data.showBrewVision
			? orNull(this.cameraApi.getMachineFillLevelTimeseries(machineId, this.range))
			: of(null);
		this.loadSubscription = forkJoin([
			orNull(this.cameraApi.getMachineLevelHistory(machineId, this.data.slotNo, this.range)),
			readings$,
			orNull(this.cameraApi.getMachineCameraRestocks(machineId, this.range))
		]).subscribe(([machine, timeseries, restocks]: [MachineLevelHistory, any, RestockComparisonResponse]) => {
			if (!machine && !timeseries && !restocks) {
				this.showLoadError();
				return;
			}
			this.machineLevels = (machine && machine.points || []).map(point => ({ x: toChartTime(point.at), y: point.pct }));
			this.readings = (timeseries && timeseries.timeseries || [])
				.filter(point => point.slotno === this.data.slotNo && point.accepted !== null && point.accepted !== undefined)
				.map(point => ({ x: toChartTime(point.timestamp), y: Math.round(point.accepted) }));
			this.restocks = (restocks && restocks.restocks || [])
				.filter(restock => restock.slot_no === this.data.slotNo && (restock.machine || this.data.showBrewVision));
			this.isLoading = false;
			this.render();
		}, () => this.showLoadError());
	}

	private showLoadError(): void {
		this.chart = null;
		this.hasData = false;
		this.isLoading = false;
		this.loadError = 'This canister\'s history could not be loaded. Try again in a moment.';
		this.cdr.detectChanges();
	}

	private render(): void {
		const series = [];
		const colors = [];
		if (this.view !== 'machine' && this.readings.length) {
			series.push({ name: 'BrewVision', data: this.readings });
			colors.push(BREWVISION_COLOR);
		}
		if (this.view !== 'brewvision' && this.machineLevels.some(point => point.y !== null)) {
			series.push({ name: 'Machine', data: this.machineLevels });
			colors.push(MACHINE_COLOR);
		}
		this.hasData = series.length > 0;
		this.chart = buildChart(series, colors, this.restocks);
		this.cdr.detectChanges();
	}
}

// Times are the machine's local time with no offset; the chart draws them as written.
function toChartTime(naiveIso: string): number {
	return new Date(naiveIso).getTime();
}

function buildChart(series: any[], colors: string[], restocks: RestockComparison[]): Record<string, any> {
	const restockLines = restocks.map(restock => ({
		x: toChartTime(restockTime(restock)),
		borderColor: RESTOCK_COLOR,
		strokeDashArray: restock.machine ? 0 : 4,
		label: {
			text: restock.machine ? formatBags(restock.machine.bags_used) : 'Not logged',
			orientation: 'horizontal',
			// Levels sit near the top right after a restock; the label stays clear of them.
			position: 'bottom',
			offsetY: -4,
			borderColor: RESTOCK_COLOR,
			style: { color: '#334155', background: '#f1f5f9', fontSize: '10px' }
		}
	}));
	return {
		series,
		chart: { type: 'line', height: 260, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
		colors,
		stroke: { curve: 'straight', width: 2 },
		dataLabels: { enabled: false },
		markers: { size: 0, hover: { size: 4 } },
		legend: { show: false },
		grid: { borderColor: '#f1f5f9', strokeDashArray: 0 },
		xaxis: {
			type: 'datetime',
			labels: { datetimeUTC: false, style: { colors: '#94a3b8', fontSize: '11px' } },
			axisBorder: { show: false },
			axisTicks: { show: false }
		},
		yaxis: {
			min: 0,
			max: 100,
			tickAmount: 4,
			labels: { formatter: (value: number) => formatPct(value), style: { colors: '#94a3b8', fontSize: '11px' } }
		},
		tooltip: {
			shared: true,
			x: { format: 'MMM d, h:mm TT' },
			y: { formatter: (value: number) => (value === null || value === undefined ? '—' : formatPct(value)) }
		},
		annotations: { xaxis: restockLines }
	};
}
